import React, { useState } from 'react'
import cx from 'classnames'
import { RichTextMicroCopy } from 'pages/news/components/newsDetail/components/rich-text/RichText'
import RichTextType from 'models/types/ts/richTextType'
import UserContextModel from 'models/UserContextModel'
import { ClipLoader } from 'react-spinners'
import { getPlainTextFromRichText } from '../../../../utils'
import style from './PlexAmountInput.module.scss'

interface PlexAmountInputProps {
    plexAmount: number
    setPlexAmount: React.Dispatch<React.SetStateAction<number>>
    plexBalance: UserContextModel['plexBalance']
    placeholder: string
    hasError: boolean
    errorMessage?: string
    balanceText: RichTextType
    zeroPlexMessage: string
    needMorePlexText: RichTextType
    insufficientPlexText?: RichTextType
}

const PlexAmountInput: React.FC<PlexAmountInputProps> = ({
    plexAmount,
    setPlexAmount,
    plexBalance,
    placeholder,
    hasError,
    errorMessage,
    zeroPlexMessage,
    balanceText,
    needMorePlexText,
    insufficientPlexText,
}) => {
    const numberFormatter = new Intl.NumberFormat()

    const plexBalanceNumber = Number(plexBalance) || 0

    // Single state for internal validation
    const [internalError, setInternalError] = useState<string | null>(null)

    // Default error messages
    const invalidAmountMessage = 'Please enter an amount greater than 0'
    const insufficientBalanceMessage = insufficientPlexText
        ? getPlainTextFromRichText(insufficientPlexText)
        : 'Insufficient PLEX balance'

    // Validate amount and update internal error state
    const validateAmount = (amount: number, showError = true): void => {
        // Convert to number to ensure proper comparison
        const numAmount = Number(amount)

        // Check if amount is valid (greater than 0)
        if (numAmount <= 0) {
            if (showError) {
                setInternalError(invalidAmountMessage)
            }
            return
        }

        // Then check if amount exceeds balance
        if (numAmount > plexBalanceNumber && showError) {
            // Set error message for insufficient balance
            setInternalError(insufficientBalanceMessage)
        } else {
            // Valid amount, clear error message
            setInternalError(null)
        }
    }

    const handlePresetAmountClick = (amount: number): void => {
        setPlexAmount(amount)
        // Preset buttons always select valid amounts
        setInternalError(null)
    }

    // Determine which error to display (internal or external)
    const shouldShowError = internalError !== null || hasError
    const displayErrorMessage = internalError || errorMessage

    let formattedPlexBalance

    if (plexBalance === 'N/A') {
        formattedPlexBalance = 'N/A'
    } else if (plexBalance === undefined) {
        formattedPlexBalance = <ClipLoader size={18} color="#F67C0F" loading />
    } else {
        formattedPlexBalance = numberFormatter.format(plexBalanceNumber)
    }

    return (
        <div
            className={style.plexInputWrapper}
            data-disabled={plexBalanceNumber === 0}
        >
            {/* PLEX Input Field */}
            <div
                className={cx(style.plexInputContainer, {
                    [style.inputError]: shouldShowError,
                })}
            >
                <span className={style.inputWrap}>
                    <span className={style.widthGenerator}>
                        {plexBalanceNumber === 0
                            ? zeroPlexMessage
                            : plexAmount || placeholder}
                    </span>
                    <input
                        placeholder={
                            plexBalanceNumber === 0
                                ? zeroPlexMessage
                                : placeholder
                        }
                        disabled={plexBalanceNumber === 0}
                        min="0" // Set minimum to 1 to indicate only positive values
                        value={
                            plexAmount
                                ? Number(plexAmount).toString()
                                : undefined
                        }
                        onChange={(event) => {
                            if (
                                ((event.nativeEvent as unknown) as {
                                    data: string
                                }).data === '-'
                            ) {
                                setPlexAmount((prev) => prev)
                            } else if (event.target.value === '') {
                                setPlexAmount(() => 0)
                            } else {
                                const value = Number(
                                    (event.target.value || '').replaceAll(
                                        '-',
                                        ''
                                    ) || 0
                                )
                                // Don't show errors while typing, but clear them if valid
                                if (value > 0 && value <= plexBalanceNumber) {
                                    setPlexAmount((prev) => value)
                                    setInternalError(null)
                                } else {
                                    setPlexAmount((prev) => prev)
                                }
                            }
                        }}
                        onBlur={() => {
                            // Only validate on blur if there's actually a value
                            if (plexAmount !== undefined) {
                                validateAmount(plexAmount)
                            }
                        }}
                        type="number"
                        id="plexAmount"
                        name="plexAmount"
                        pattern="^[1-9][0-9]*" // Pattern already enforces non-zero start
                    />
                </span>
                {plexBalanceNumber > 0 ? (
                    <span style={{ userSelect: 'none' }}>PLEX</span>
                ) : null}
            </div>

            {/* Error Message */}
            {shouldShowError && displayErrorMessage && (
                <div className={style.errorMessage}>{displayErrorMessage}</div>
            )}

            {/* PLEX Balance */}
            <div className={style.plexBalance}>
                <div className={style.plexBalanceText}>
                    <div>
                        <RichTextMicroCopy document={balanceText} />
                    </div>
                    <div>{formattedPlexBalance}</div>
                </div>
                <a
                    target="_blank"
                    href="https://store.eveonline.com#plex"
                    rel="noreferrer"
                >
                    <RichTextMicroCopy document={needMorePlexText} />
                </a>
            </div>

            {/* Amount Presets */}
            <div className={style.plexButtons}>
                {[10, 100, 1000, plexBalanceNumber].map((amount) => {
                    return (
                        <button
                            key={amount}
                            type="button"
                            onClick={() => handlePresetAmountClick(amount)}
                            disabled={
                                amount > plexBalanceNumber || amount === 0
                            }
                        >
                            {numberFormatter.format(amount)} PLEX
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export default PlexAmountInput
